/**
 * EmbedModulePage — встраиваемая версия страницы модуля
 * apps/web/src/pages/embed/EmbedModulePage.tsx
 * 
 * Навигация: /embed/collection/:id/module/:moduleId
 * Кнопка "Назад" → /embed/collection/:id
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../../stores/collections';

export default function EmbedModulePage() {
  const { id, moduleId } = useParams<{ id: string; moduleId: string }>();
  const navigate = useNavigate();

  const currentCollection = useCollectionsStore((state) => state.currentCollection);
  const currentProgress = useCollectionsStore((state) => state.currentProgress);
  const loadCollectionDetail = useCollectionsStore((state) => state.loadCollectionDetail);
  const isLoading = useCollectionsStore((state) => state.isLoading);

  const [module, setModule] = useState<any>(null);
  const [moduleProgress, setModuleProgress] = useState<any>(null);

  useEffect(() => {
    if (id && !currentCollection) {
      loadCollectionDetail(id);
    }
  }, [id, currentCollection, loadCollectionDetail]);

  useEffect(() => {
    if (currentCollection && moduleId) {
      for (const section of currentCollection.sections) {
        const found = section.modules.find((m: any) => m.id === moduleId);
        if (found) { setModule(found); break; }
      }
    }
    if (currentProgress && moduleId) {
      setModuleProgress(currentProgress.moduleProgress?.[moduleId] || null);
    }
  }, [currentCollection, currentProgress, moduleId]);

  // Embed-навигация
  const handleBack = () => navigate(`/embed/collection/${id}`);
  const handleLesson = () => {
    if (module?.lesson?.id) {
      navigate(`/embed/collection/${id}/lesson/${moduleId}?lessonId=${module.lesson.id}`);
    }
  };
  const handleTest = () => navigate(`/embed/collection/${id}/play/${moduleId}`);

  if (isLoading || !module) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasLesson = !!module.lesson;
  const lessonViewed = moduleProgress?.lessonViewed || false;
  const testCompleted = moduleProgress?.completed || false;
  const bestScore = moduleProgress?.bestScore || 0;
  const attempts = moduleProgress?.attempts || 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100"
          >
            ←
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-gray-900">{module.name}</h1>
            <p className="text-sm text-gray-500">{currentCollection?.name}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {module.description && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-gray-600">{module.description}</p>
          </div>
        )}

        {/* Lesson */}
        {hasLesson && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-2xl">📖</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">Урок</h3>
                  {lessonViewed && <span className="text-green-500 text-sm">✓ Просмотрен</span>}
                </div>
                <p className="text-sm text-gray-500 mt-1">Изучите материал перед тестом</p>
              </div>
            </div>
            <button
              onClick={handleLesson}
              className="w-full mt-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-xl transition-colors"
            >
              {lessonViewed ? 'Читать ещё раз' : 'Читать урок'}
            </button>
          </div>
        )}

        {/* Test */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center text-2xl">📝</div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">Тест</h3>
                {testCompleted && <span className="text-green-500 text-sm">✓ Пройден</span>}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {module.quizIds?.length || 1} викторин • Порог: {module.requiredScore || 70}%
              </p>
              {attempts > 0 && (
                <p className="text-sm text-gray-400 mt-1">
                  Лучший: {bestScore}% • Попыток: {attempts}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleTest}
            disabled={hasLesson && !lessonViewed}
            className={`w-full mt-4 py-3 font-medium rounded-xl transition-colors ${
              hasLesson && !lessonViewed
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-green-500 hover:bg-green-600 text-white'
            }`}
          >
            {testCompleted ? 'Пройти снова' : 'Пройти тест'}
          </button>
          {hasLesson && !lessonViewed && (
            <p className="text-xs text-center text-gray-400 mt-2">Сначала изучите урок</p>
          )}
        </div>
      </div>
    </div>
  );
}
